import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiFetch } from "../../api/client";
import { COLORS } from "../../theme";
import Card from "../../components/ui/Card";
import Btn from "../../components/ui/Btn";
import Input from "../../components/ui/Input";
import Badge from "../../components/ui/Badge";
import Modal from "../../components/ui/Modal";
import EstadoCarregando from "../../components/ui/EstadoCarregando";
import EstadoVazio from "../../components/ui/EstadoVazio";
import { ThFiltroTexto, ThFiltroSelect, ThSimples, BarraResumoFiltro } from "../../components/ui/ThFiltro";

const VAZIO = { nome: "", gestorNome: "", gestorEmail: "", gestorSenha: "", empresas: [{ nome: "", cnpj: "" }] };

export default function AdminClientes() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [empresasTenant, setEmpresasTenant] = useState(null); // tenant sendo gerenciado
  const [usuariosTenant, setUsuariosTenant] = useState(null); // tenant sendo gerenciado

  const [filtroNome, setFiltroNome] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch("GET", "/api/admin/tenants", undefined, token);
      setTenants(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const atualizarEmpresa = (i, campo, valor) => {
    const empresas = [...form.empresas];
    empresas[i] = { ...empresas[i], [campo]: valor };
    setForm({ ...form, empresas });
  };

  const adicionarEmpresa = () => setForm({ ...form, empresas: [...form.empresas, { nome: "", cnpj: "" }] });
  const removerEmpresa = (i) => setForm({ ...form, empresas: form.empresas.filter((_, idx) => idx !== i) });

  const salvar = async () => {
    setErro("");
    const empresasValidas = form.empresas.filter((e) => e.nome.trim());
    if (!form.nome || !form.gestorNome || !form.gestorEmail || !form.gestorSenha) {
      setErro("Preencha nome do cliente e os dados do gestor");
      return;
    }
    if (empresasValidas.length === 0) {
      setErro("Cadastre pelo menos uma empresa (CNPJ)");
      return;
    }
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/admin/tenants", {
        nome: form.nome,
        empresas: empresasValidas,
        gestor: { nome: form.gestorNome, email: form.gestorEmail, senha: form.gestorSenha },
      }, token);
      setModalOpen(false);
      setForm(VAZIO);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (tenant) => {
    await apiFetch("PUT", `/api/admin/tenants/${tenant.id}`, {
      nome: tenant.nome, ativo: !tenant.ativo,
    }, token);
    carregar();
  };

  const tenantsFiltrados = tenants.filter((t) => {
    if (filtroNome && !t.nome.toLowerCase().includes(filtroNome.toLowerCase())) return false;
    if (filtroStatus && (filtroStatus === "ativo") !== !!t.ativo) return false;
    return true;
  });
  const filtrosAtivos = Boolean(filtroNome || filtroStatus);
  const limparFiltros = () => { setFiltroNome(""); setFiltroStatus(""); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Clientes
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Grupos empresariais e conservadoras que usam o PontoZipar.
          </p>
        </div>
        <Btn onClick={() => { setForm(VAZIO); setErro(""); setModalOpen(true); }}>+ Novo cliente</Btn>
      </div>

      {tenants.length > 0 && (
        <BarraResumoFiltro
          total={tenantsFiltrados.length}
          totalGeral={tenants.length}
          unidade="cliente(s)"
          filtrosAtivos={filtrosAtivos}
          onLimpar={limparFiltros}
        />
      )}

      <Card style={{ padding: 0 }}>
        {loading ? (
          <EstadoCarregando />
        ) : tenants.length === 0 ? (
          <EstadoVazio icon="briefcase" texto="Nenhum cliente cadastrado ainda." />
        ) : tenantsFiltrados.length === 0 ? (
          <EstadoVazio icon="briefcase" texto="Nenhum cliente encontrado com esse filtro." />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <ThFiltroTexto label="Cliente" value={filtroNome} onChange={setFiltroNome} placeholder="Buscar..." minWidth={170} />
                <ThSimples label="Empresas" />
                <ThSimples label="Usuários" />
                <ThSimples label="Funcionários" />
                <ThSimples label="Módulo Rotas/NFC" />
                <ThFiltroSelect
                  label="Status" value={filtroStatus} onChange={setFiltroStatus} minWidth={110}
                  opcoes={[{ value: "", label: "Todos" }, { value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]}
                />
                <ThSimples label="" />
              </tr>
            </thead>
            <tbody>
              {tenantsFiltrados.map((t) => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{t.nome}</td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>
                    <button
                      onClick={() => setEmpresasTenant(t)}
                      style={{ background: "transparent", border: "none", color: COLORS.primary, fontWeight: 600, fontSize: 13.5, cursor: "pointer", padding: 0 }}
                    >
                      {t.total_empresas} {t.total_empresas === 1 ? "empresa" : "empresas"}
                    </button>
                  </td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>
                    <button
                      onClick={() => setUsuariosTenant(t)}
                      style={{ background: "transparent", border: "none", color: COLORS.primary, fontWeight: 600, fontSize: 13.5, cursor: "pointer", padding: 0 }}
                    >
                      {t.total_usuarios} {t.total_usuarios === 1 ? "usuário" : "usuários"}
                    </button>
                  </td>
                  <td style={{ padding: "14px 20px", fontSize: 13.5, color: COLORS.textMid }}>{t.total_funcionarios}</td>
                  <td style={{ padding: "14px 20px" }}>
                    <button
                      onClick={() => setEmpresasTenant(t)}
                      title="Habilitar ou desabilitar o módulo por empresa"
                      style={{
                        background: t.total_empresas_rotas > 0 ? COLORS.primaryBg : "transparent",
                        border: `1.5px solid ${t.total_empresas_rotas > 0 ? COLORS.primary : COLORS.border}`,
                        borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                        color: t.total_empresas_rotas > 0 ? COLORS.primary : COLORS.textMid, whiteSpace: "nowrap",
                      }}
                    >
                      {t.total_empresas_rotas > 0 ? `${t.total_empresas_rotas} de ${t.total_empresas} habilitada(s)` : "Habilitar"}
                    </button>
                  </td>
                  <td style={{ padding: "14px 20px" }}>
                    <Badge tone={t.ativo ? "success" : "neutral"}>{t.ativo ? "Ativo" : "Inativo"}</Badge>
                  </td>
                  <td style={{ padding: "14px 20px", textAlign: "right" }}>
                    <button
                      onClick={() => alternarAtivo(t)}
                      style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}
                    >
                      {t.ativo ? "Desativar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Novo cliente" width={480}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input label="Nome do grupo/cliente" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Grupo Bela Vista" />

          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginTop: 4 }}>
            <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
              Empresas (CNPJs)
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {form.empresas.map((empresa, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <Input placeholder="Nome da empresa" value={empresa.nome} onChange={(e) => atualizarEmpresa(i, "nome", e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Input placeholder="CNPJ" value={empresa.cnpj} onChange={(e) => atualizarEmpresa(i, "cnpj", e.target.value)} />
                  </div>
                  {form.empresas.length > 1 && (
                    <button
                      onClick={() => removerEmpresa(i)}
                      style={{ background: COLORS.gray, border: "none", borderRadius: 8, width: 40, height: 40, flexShrink: 0, color: COLORS.textMid, cursor: "pointer", fontSize: 15 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={adicionarEmpresa}
              style={{ background: "transparent", border: "none", color: COLORS.primary, fontWeight: 600, fontSize: 13, cursor: "pointer", padding: "8px 0 0" }}
            >
              + Adicionar empresa
            </button>
          </div>

          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginTop: 4 }}>
            <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
              Usuário gestor inicial
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Input label="Nome" value={form.gestorNome} onChange={(e) => setForm({ ...form, gestorNome: e.target.value })} placeholder="Nome do responsável" />
              <Input label="E-mail" type="email" value={form.gestorEmail} onChange={(e) => setForm({ ...form, gestorEmail: e.target.value })} placeholder="gestor@cliente.com.br" />
              <Input label="Senha" type="password" value={form.gestorSenha} onChange={(e) => setForm({ ...form, gestorSenha: e.target.value })} placeholder="••••••••" />
            </div>
          </div>

          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}

          <Btn onClick={salvar} disabled={salvando} style={{ width: "100%", marginTop: 4 }}>
            {salvando ? "Salvando..." : "Criar cliente"}
          </Btn>
        </div>
      </Modal>

      <GerenciarEmpresas tenant={empresasTenant} onClose={() => setEmpresasTenant(null)} onChange={carregar} />
      <GerenciarUsuarios tenant={usuariosTenant} onClose={() => setUsuariosTenant(null)} onChange={carregar} />
    </div>
  );
}

const PERFIL_LABEL = { admin_empresa: "Administrador", gestor: "Gestor", funcionario: "Funcionário" };
const PERFIL_TONE = { admin_empresa: "warning", gestor: "success", funcionario: "neutral" };
const USUARIO_VAZIO = { nome: "", email: "", senha: "", perfil: "admin_empresa" };

// Cria o primeiro acesso de um cliente (Admin da Empresa, gestor ou
// funcionário) direto daqui — antes disso só dava pra fazer via SQL manual.
// Perfil 'admin' (super-admin geral) fica de fora de propósito, ver admin.js.
function GerenciarUsuarios({ tenant, onClose, onChange }) {
  const { token } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [novo, setNovo] = useState(USUARIO_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      // Não existe um GET por tenant separado — a lista geral já vem com
      // tenant_id em cada linha, filtra aqui mesmo.
      const data = await apiFetch("GET", "/api/admin/usuarios", undefined, token);
      setUsuarios(data.filter((u) => u.tenant_id === tenant.id));
    } finally {
      setLoading(false);
    }
  }, [tenant, token]);

  useEffect(() => { carregar(); setNovo(USUARIO_VAZIO); setErro(""); }, [carregar, tenant]);

  const criar = async () => {
    setErro("");
    if (!novo.nome.trim() || !novo.email.trim() || !novo.senha) {
      setErro("Preencha nome, e-mail e senha");
      return;
    }
    setSalvando(true);
    try {
      await apiFetch("POST", `/api/admin/tenants/${tenant.id}/usuarios`, novo, token);
      setNovo(USUARIO_VAZIO);
      carregar();
      onChange();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (usuario) => {
    await apiFetch("PUT", `/api/admin/usuarios/${usuario.id}`, { ativo: !usuario.ativo }, token);
    carregar();
  };

  return (
    <Modal open={!!tenant} onClose={onClose} title={`Usuários — ${tenant?.nome || ""}`} width={480}>
      {loading ? (
        <EstadoCarregando padding={0} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
          {usuarios.length === 0 && (
            <div style={{ fontSize: 13.5, color: COLORS.textMid }}>Nenhum usuário cadastrado ainda.</div>
          )}
          {usuarios.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 14px" }}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{u.nome}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>{u.email}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Badge tone={PERFIL_TONE[u.perfil] || "neutral"}>{PERFIL_LABEL[u.perfil] || u.perfil}</Badge>
                <Badge tone={u.ativo ? "success" : "neutral"}>{u.ativo ? "Ativo" : "Inativo"}</Badge>
                <button
                  onClick={() => alternarAtivo(u)}
                  style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}
                >
                  {u.ativo ? "Desativar" : "Ativar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
        <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
          Criar novo usuário
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 }}>Perfil</label>
            <select
              value={novo.perfil}
              onChange={(e) => setNovo({ ...novo, perfil: e.target.value })}
              style={{ width: "100%", boxSizing: "border-box", border: `1.5px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 14, background: COLORS.bg, color: COLORS.text }}
            >
              <option value="admin_empresa">Administrador</option>
              <option value="gestor">Gestor</option>
              <option value="funcionario">Funcionário</option>
            </select>
          </div>
          <Input placeholder="Nome" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} />
          <Input placeholder="E-mail" type="email" value={novo.email} onChange={(e) => setNovo({ ...novo, email: e.target.value })} />
          <Input placeholder="Senha" type="password" value={novo.senha} onChange={(e) => setNovo({ ...novo, senha: e.target.value })} />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <Btn onClick={criar} disabled={salvando} style={{ width: "100%" }}>
            {salvando ? "Criando..." : "Criar usuário"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function GerenciarEmpresas({ tenant, onClose, onChange }) {
  const { token } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nova, setNova] = useState({ nome: "", cnpj: "" });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    try {
      const data = await apiFetch("GET", `/api/admin/tenants/${tenant.id}/empresas`, undefined, token);
      setEmpresas(data);
    } finally {
      setLoading(false);
    }
  }, [tenant, token]);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionar = async () => {
    setErro("");
    if (!nova.nome.trim()) { setErro("Informe o nome da empresa"); return; }
    setSalvando(true);
    try {
      await apiFetch("POST", `/api/admin/tenants/${tenant.id}/empresas`, nova, token);
      setNova({ nome: "", cnpj: "" });
      carregar();
      onChange();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (empresa) => {
    await apiFetch("PUT", `/api/admin/tenants/${tenant.id}/empresas/${empresa.id}`, { ativo: !empresa.ativo }, token);
    carregar();
  };

  const alternarRotas = async (empresa) => {
    await apiFetch("PUT", `/api/admin/tenants/${tenant.id}/empresas/${empresa.id}`, { rotas_ativo: !empresa.rotas_ativo }, token);
    carregar();
    onChange();
  };

  return (
    <Modal open={!!tenant} onClose={onClose} title={`Empresas — ${tenant?.nome || ""}`} width={480}>
      {loading ? (
        <EstadoCarregando padding={0} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 18 }}>
          {empresas.length === 0 && (
            <div style={{ fontSize: 13.5, color: COLORS.textMid }}>Nenhuma empresa cadastrada.</div>
          )}
          {empresas.map((e) => (
            <div key={e.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{e.nome}</div>
                  <div style={{ fontSize: 12, color: COLORS.textLight }}>{e.cnpj || "sem CNPJ"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Badge tone={e.ativo ? "success" : "neutral"}>{e.ativo ? "Ativa" : "Inativa"}</Badge>
                  <button
                    onClick={() => alternarAtivo(e)}
                    style={{ background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "5px 10px", fontSize: 12, fontWeight: 600, color: COLORS.textMid, cursor: "pointer" }}
                  >
                    {e.ativo ? "Desativar" : "Ativar"}
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.border}` }}>
                <span style={{ fontSize: 12, color: COLORS.textMid }}>Módulo Rotas/NFC</span>
                <button
                  onClick={() => alternarRotas(e)}
                  style={{
                    background: e.rotas_ativo ? COLORS.primaryBg : "transparent", border: `1.5px solid ${e.rotas_ativo ? COLORS.primary : COLORS.border}`,
                    borderRadius: 8, padding: "4px 10px", fontSize: 11.5, fontWeight: 600,
                    color: e.rotas_ativo ? COLORS.primary : COLORS.textMid, cursor: "pointer",
                  }}
                >
                  {e.rotas_ativo ? "Habilitado" : "Desabilitado"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 16 }}>
        <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
          Adicionar nova empresa
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Input placeholder="Nome da empresa" value={nova.nome} onChange={(e) => setNova({ ...nova, nome: e.target.value })} />
          <Input placeholder="CNPJ" value={nova.cnpj} onChange={(e) => setNova({ ...nova, cnpj: e.target.value })} />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <Btn onClick={adicionar} disabled={salvando} style={{ width: "100%" }}>
            {salvando ? "Salvando..." : "Adicionar empresa"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
